import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Search, Filter as FilterIcon } from 'lucide-react';
import AccountForm from './AccountForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import { ActiveStatusPill, CategoryPill } from '../../../shared/Pill';
import { formatBsb } from '../../../shared/paymentOptions';
import '../../../shared/ListRowHover.css';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

const DEFAULT_FILTERS = { status: 'all', account_category_id: '', account_class_id: '', account_type_id: '' };
const FIELD_FILTER_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_FILTER_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };
const HEAD = { padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' };
const CELL = { padding: '6px 8px', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

// Both the active and the deactivated rows of a reference list.
async function fetchAll(url) {
  const [active, inactive] = await Promise.all([
    axios.get(url, { params: { limit: 100, status: 'active' } }),
    axios.get(url, { params: { limit: 100, status: 'inactive' } }),
  ]);
  return [...(active.data.data || []), ...(inactive.data.data || [])];
}

// Accounting › Lists › Accounts (issue #341) — the Chart of Accounts'
// accounts, each under an Account Type › Class › Category. An Account under
// a bank Type holds bank details; Company Settings prints one of them on
// Prepay Quotes and Sales Orders, and payments record which one they were
// paid into.
function AccountTable() {
  const { canCreate, canUpdate } = useScreenAccess();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [classes, setClasses] = useState([]);
  const [types, setTypes] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filterDraft, setFilterDraft] = useState(null); // null = panel closed
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { setFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { setFilters(DEFAULT_FILTERS); closeFilterPanel(); };

  const fetchRows = () => {
    setLoading(true);
    const params = { search, status: filters.status };
    ['account_category_id', 'account_class_id', 'account_type_id'].forEach((k) => { if (filters[k]) params[k] = filters[k]; });
    axios.get('/api/accounts', { params })
      .then((res) => setRows(res.data.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filters]);

  useEffect(() => {
    fetchAll('/api/account-categories').then(setCategories).catch(() => setCategories([]));
    fetchAll('/api/account-classes').then(setClasses).catch(() => setClasses([]));
    fetchAll('/api/account-types').then(setTypes).catch(() => setTypes([]));
  }, []);

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const run = async (request, failMessage) => {
    try {
      await request();
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, failMessage);
    }
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate Account "${item.account_number} ${item.account_name}"?`, { confirmLabel: 'Deactivate' }))) return;
    run(() => axios.patch(`/api/accounts/${item.id}/deactivate`), 'Deactivate failed');
  };

  const handleReactivate = (item) => run(() => axios.patch(`/api/accounts/${item.id}/reactivate`), 'Reactivate failed');

  const handleDelete = async (item) => {
    if (!(await askConfirm(`Delete Account "${item.account_number} ${item.account_name}"? This cannot be undone.`, { confirmLabel: 'Delete', destructive: true }))) return;
    run(() => axios.delete(`/api/accounts/${item.id}`), 'Delete failed');
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/accounts/${editItem.id}`, data);
      } else {
        await axios.post('/api/accounts', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  // Class and Type choices narrow to the Category (and Class) picked above them.
  const renderDrawerFields = (values, setField) => {
    const classChoices = classes.filter((c) => !values.account_category_id || String(c.account_category_id) === String(values.account_category_id));
    const typeChoices = types.filter((t) => (!values.account_class_id || String(t.account_class_id) === String(values.account_class_id))
      && (!values.account_category_id || String(t.account_class?.account_category?.id) === String(values.account_category_id)));
    return (
      <>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={FIELD_FILTER_LABEL_STYLE}>Status</span>
          <select className="styled-input" value={values.status} onChange={(e) => setField('status', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Deactivated</option>
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={FIELD_FILTER_LABEL_STYLE}>Account Category</span>
          <select className="styled-input" value={values.account_category_id} onChange={(e) => { setField('account_category_id', e.target.value); setField('account_class_id', ''); setField('account_type_id', ''); }} style={FIELD_FILTER_INPUT_STYLE}>
            <option value="">All</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.category_name}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={FIELD_FILTER_LABEL_STYLE}>Account Class</span>
          <select className="styled-input" value={values.account_class_id} onChange={(e) => { setField('account_class_id', e.target.value); setField('account_type_id', ''); }} style={FIELD_FILTER_INPUT_STYLE}>
            <option value="">All</option>
            {classChoices.map((c) => <option key={c.id} value={c.id}>{c.class_name}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={FIELD_FILTER_LABEL_STYLE}>Account Type</span>
          <select className="styled-input" value={values.account_type_id} onChange={(e) => setField('account_type_id', e.target.value)} style={FIELD_FILTER_INPUT_STYLE}>
            <option value="">All</option>
            {typeChoices.map((t) => <option key={t.id} value={t.id}>{t.account_number} {t.type_name}</option>)}
          </select>
        </label>
      </>
    );
  };

  const bankDetails = (item) => {
    if (!item.account_type?.is_bank) return '—';
    const parts = [item.bank_bsb && `BSB ${formatBsb(item.bank_bsb)}`, item.bank_account_number && `Acc ${item.bank_account_number}`].filter(Boolean);
    return parts.length ? parts.join(' · ') : <span style={{ color: '#b45309' }}>Not filled in</span>;
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Accounts</h2>
        {canCreate && (
          <button
            type="button"
            onClick={handleAdd}
            disabled={showForm}
            title={showForm ? 'Finish editing first' : undefined}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 6, border: 'none', background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
          >
            <Plus size={15} strokeWidth={2.5} />
            Add Account
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '16px 24px 0', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fff', border: '1px solid #d0d5dd', borderRadius: 8, maxWidth: 280 }}>
          <Search size={14} strokeWidth={1.75} color="#888" />
          <input
            type="text"
            placeholder="Search number or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ border: 'none', outline: 'none', fontSize: 14, width: '100%' }}
          />
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid #bfc8d6', background: activeFilterCount ? '#eef1f5' : '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the Accounts list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <AccountForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            accountTypes={types}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '14px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '8%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '6%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '4%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ ...HEAD, textAlign: 'right' }}>Acct #</th>
              <th style={HEAD}>Name</th>
              <th style={HEAD}>Type</th>
              <th style={HEAD}>Class</th>
              <th style={HEAD}>Category</th>
              <th style={HEAD}>Bank Details</th>
              <th style={{ ...HEAD, textAlign: 'center' }}>In Use</th>
              <th style={{ ...HEAD, textAlign: 'center' }}>Status</th>
              <th style={{ ...HEAD, textAlign: 'center' }} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No Accounts match.</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => { if (!showForm && canUpdate) handleEdit(item); }}
                style={{ cursor: showForm || !canUpdate ? 'default' : 'pointer', opacity: item.is_active ? 1 : 0.55 }}
                title={showForm || !canUpdate ? undefined : 'Click to edit'}
              >
                <td style={{ ...CELL, textAlign: 'right' }}>{item.account_number}</td>
                <td style={CELL}>
                  <span style={{ fontWeight: 600 }}>{item.account_name}</span>
                  {item.is_payment_option && (
                    <span style={{ marginLeft: 8 }}>
                      <CategoryPill value="Payment Option" color="#1976d2" title="Used by a Payment Option (Receivables › Payment Options)" />
                    </span>
                  )}
                </td>
                <td style={CELL}>{item.account_type?.type_name || ''}</td>
                <td style={CELL}>{item.account_type?.account_class?.class_name || ''}</td>
                <td style={CELL}>{item.account_type?.account_class?.account_category?.category_name || ''}</td>
                <td style={{ ...CELL, fontSize: 13 }}>{bankDetails(item)}</td>
                <td style={{ ...CELL, textAlign: 'center', fontSize: 13 }} title={item.usage_count ? 'Payments recorded into this Account' : undefined}>{item.usage_count || '—'}</td>
                <td style={{ ...CELL, textAlign: 'center' }}><ActiveStatusPill active={item.is_active} /></td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                      item.is_active
                        ? { key: 'deactivate', label: 'Deactivate', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDeactivate(item) }
                        : { key: 'reactivate', label: 'Reactivate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReactivate(item) },
                      {
                        key: 'delete',
                        label: 'Delete',
                        danger: true,
                        disabled: showForm || item.usage_count > 0 || item.is_payment_option,
                        disabledTitle: showForm ? 'Finish editing first'
                          : item.usage_count > 0 ? 'Payments are recorded into this Account — deactivate it instead'
                            : 'Used by a Payment Option — pick another Account in Receivables › Payment Options first',
                        onClick: () => handleDelete(item),
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AccountTable;
